/*
 * Dorm Dash A2, delivery calendar.
 *
 * Julie asked for a calendar showing the days she cannot deliver. A native
 * <input type="date"> cannot grey out individual days, so this draws a month grid
 * next to one and writes the chosen day into it.
 *
 * THE THREE RULES OF THIS FILE
 *
 * 1. The native input is the control. This script only ever writes a value into it.
 *    It attaches no submit handler, never calls setCustomValidity, and never touches
 *    min or max. Every button it renders is type="button", so nothing here can submit
 *    a form even by accident. A blocked form does not post, and that once cost Julie
 *    four requests from the same person, none of which reached her. See the comment
 *    above the date field in sections/dda2-custom-form.liquid.
 *
 * 2. Today comes from the server, never from new Date(). Liquid computes "today" in
 *    the shop's timezone. A visitor in California at 10pm sees a different date. If
 *    this script wrote a day below the input's min, the input would go rangeUnderflow,
 *    and because it is required the browser would refuse to submit with no visible
 *    reason. commit() below is the only place a value is ever assigned, and it checks
 *    min before writing.
 *
 * 3. Blackout days are aria-disabled, not disabled. A disabled button is skipped by
 *    keyboard and left out of screen reader element lists, so a blind visitor would
 *    just find the 26th missing with no explanation. aria-disabled keeps it reachable
 *    and readable, and activating it says why instead of selecting it.
 */
(function () {
  'use strict';

  if (window.__dda2Calendar) return;
  window.__dda2Calendar = true;

  var DAY_MS = 86400000;
  var WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  /* ---------- dates, in UTC, with ISO strings as the only currency ---------- */

  function fromISO(s) {
    var p = String(s || '').split('-');
    if (p.length !== 3) return null;
    var d = new Date(Date.UTC(+p[0], +p[1] - 1, +p[2]));
    return isNaN(d.getTime()) ? null : d;
  }

  function toISO(d) {
    return d.toISOString().slice(0, 10);
  }

  function addDays(d, n) {
    return new Date(d.getTime() + n * DAY_MS);
  }

  // Always anchored to the first of a month, so this can never overflow the way
  // adding a month to the 31st would.
  function monthStart(d, offset) {
    return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + (offset || 0), 1));
  }

  function sameMonth(a, b) {
    return a.getUTCFullYear() === b.getUTCFullYear() && a.getUTCMonth() === b.getUTCMonth();
  }

  // en-US and an explicit UTC timezone, so the label can never disagree with the value.
  function label(d, opts) {
    var o = { timeZone: 'UTC' };
    for (var k in opts) o[k] = opts[k];
    try {
      return d.toLocaleDateString('en-US', o);
    } catch (e) {
      return toISO(d);
    }
  }

  function longDate(d) {
    return label(d, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  }

  function monthTitle(d) {
    return label(d, { month: 'long', year: 'numeric' });
  }

  /* ---------- one calendar ---------- */

  function Calendar(root) {
    this.root = root;
    this.input = document.getElementById(root.getAttribute('data-dda2-input') || '');

    // No input means nothing to write into. Leave the page exactly as it was.
    if (!this.input) return;

    this.todayISO = root.getAttribute('data-dda2-today') || '';
    this.leadISO = root.getAttribute('data-dda2-lead-date') || '';
    this.leadDays = root.getAttribute('data-dda2-lead-days') || '7';

    /* 'advise' (the custom form): a short-notice day is selectable and merely flagged,
       because that request is a conversation with Julie. 'block' (a product page):
       the order-fields validator refuses a short-notice date at submit, so the grid
       must refuse it too. A day the calendar offers and the validator then rejects is
       worse than a day the calendar never offered. */
    this.leadMode = root.getAttribute('data-dda2-lead-mode') === 'block' ? 'block' : 'advise';
    this.months = parseInt(root.getAttribute('data-dda2-months'), 10) || 12;

    var today = fromISO(this.todayISO);
    if (!today) return;
    this.today = today;
    this.lastMonth = monthStart(today, this.months);

    this.grid = root.querySelector('[data-dda2-cal-grid]');
    this.caption = root.querySelector('.dda2-cal__month');
    this.status = root.querySelector('[data-dda2-cal-status]');
    this.prev = root.querySelector('[data-dda2-cal-prev]');
    this.next = root.querySelector('[data-dda2-cal-next]');
    if (!this.grid) return;

    this.blackouts = this.readBlackouts();

    // The advisory note and the flag both live in the form, not in the calendar.
    var form = this.input.form || document;
    this.note = form.querySelector('.dda2-cb-blackout');
    this.flag = form.querySelector('[data-dda2-cb-flag]');

    var start = fromISO(this.input.value) || today;
    this.cursor = monthStart(start);
    this.focusISO = this.input.value || this.todayISO;

    this.bind();
    this.render();
    this.syncFromInput();

    root.hidden = false;
  }

  Calendar.prototype.readBlackouts = function () {
    var map = {};
    var host = this.root.parentNode &&
      this.root.parentNode.querySelector('[data-dda2-blackout-list]');
    if (!host) return map;
    var spans = host.querySelectorAll('[data-date]');
    Array.prototype.forEach.call(spans, function (s) {
      var iso = s.getAttribute('data-date');
      if (iso) map[iso] = s.getAttribute('data-reason') || 'Not a delivery day';
    });
    return map;
  };

  /* The ONLY place input.value is ever assigned. Rule 2 lives here. */
  Calendar.prototype.commit = function (iso) {
    var min = this.input.getAttribute('min') || '';
    if (min && iso < min) return false;

    this.input.value = iso;
    this.input.dispatchEvent(new Event('input', { bubbles: true }));
    this.input.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  };

  Calendar.prototype.classify = function (iso) {
    if (iso < this.todayISO) return 'past';
    if (this.blackouts[iso]) return 'blocked';
    if (this.leadISO && iso < this.leadISO) {
      return this.leadMode === 'block' ? 'early' : 'rush';
    }
    return 'open';
  };

  // Why a day cannot be taken, in Julie's terms. Empty for a day that can.
  Calendar.prototype.reasonFor = function (iso, kind) {
    if (kind === 'blocked') return this.blackouts[iso];
    if (kind === 'early') return 'Julie needs at least ' + this.leadDays + " days' notice";
    return '';
  };

  Calendar.prototype.say = function (msg) {
    if (this.status) this.status.textContent = msg;
  };

  /* ---------- render ---------- */

  Calendar.prototype.render = function () {
    var self = this;
    var cursor = this.cursor;
    var selected = this.input.value;

    if (this.caption) this.caption.textContent = monthTitle(cursor);
    if (this.prev) this.prev.disabled = !(monthStart(cursor, -1) >= monthStart(this.today));
    if (this.next) this.next.disabled = !(monthStart(cursor, 1) <= this.lastMonth);

    // Keep the roving tabindex on a day inside the month being drawn.
    var focus = fromISO(this.focusISO);
    if (!focus || !sameMonth(focus, cursor)) {
      this.focusISO = sameMonth(this.today, cursor) ? this.todayISO : toISO(cursor);
    }

    var head = '<thead><tr>';
    WEEKDAYS.forEach(function (d) {
      head += '<th scope="col"><abbr title="' + d + '">' + d.slice(0, 2) + '</abbr></th>';
    });
    head += '</tr></thead>';

    var day = addDays(cursor, -cursor.getUTCDay());
    var body = '<tbody>';

    for (var w = 0; w < 6; w++) {
      // Stop once a whole week has moved past the month.
      if (w > 0 && !sameMonth(day, cursor)) break;
      body += '<tr role="row">';
      for (var i = 0; i < 7; i++) {
        body += self.cell(day, cursor, selected);
        day = addDays(day, 1);
      }
      body += '</tr>';
    }
    body += '</tbody>';

    this.grid.innerHTML = head + body;
  };

  Calendar.prototype.cell = function (day, cursor, selected) {
    if (!sameMonth(day, cursor)) {
      return '<td role="gridcell" class="dda2-cal__pad"></td>';
    }

    var iso = toISO(day);
    var kind = this.classify(iso);
    var isSel = selected === iso;
    var name = longDate(day);
    var attrs = '';

    if (kind === 'blocked' || kind === 'early') {
      // Reachable and readable, never silently absent. Rule 3.
      name += ', unavailable, ' + this.reasonFor(iso, kind);
      attrs = ' aria-disabled="true"';
    } else if (kind === 'past') {
      attrs = ' disabled';
    } else if (kind === 'rush') {
      name += ", sooner than Julie's usual " + this.leadDays + ' days';
    }

    return '<td role="gridcell" aria-selected="' + (isSel ? 'true' : 'false') + '">' +
      '<button type="button" class="dda2-cal__day dda2-cal__day--' + kind +
      (isSel ? ' is-selected' : '') + '"' +
      ' data-iso="' + iso + '" data-kind="' + kind + '"' +
      ' tabindex="' + (iso === this.focusISO ? '0' : '-1') + '"' +
      ' aria-label="' + name + '"' + attrs + '>' +
      day.getUTCDate() + '</button></td>';
  };

  /* ---------- interaction ---------- */

  Calendar.prototype.choose = function (iso, kind) {
    if (kind === 'past') return;

    if (kind === 'blocked') {
      var d = fromISO(iso);
      this.say('Julie does not deliver on ' +
        label(d, { weekday: 'long', month: 'long', day: 'numeric' }) + '. It is a ' +
        this.blackouts[iso] + '. Please choose another day.');
      return;
    }

    if (kind === 'early') {
      this.say(label(fromISO(iso), { weekday: 'long', month: 'long', day: 'numeric' }) +
        ' is too soon. ' + this.reasonFor(iso, kind) +
        ' to put the box together. Please choose a later day.');
      return;
    }

    if (this.commit(iso)) {
      this.say('Selected ' + longDate(fromISO(iso)) + '.');
    }
  };

  Calendar.prototype.moveFocus = function (iso) {
    var d = fromISO(iso);
    if (!d) return;
    if (d < monthStart(this.today)) return;
    if (monthStart(d) > this.lastMonth) return;

    this.focusISO = iso;
    if (!sameMonth(d, this.cursor)) {
      this.cursor = monthStart(d);
      this.render();
    } else {
      var btns = this.grid.querySelectorAll('[data-iso]');
      Array.prototype.forEach.call(btns, function (b) {
        b.setAttribute('tabindex', b.getAttribute('data-iso') === iso ? '0' : '-1');
      });
    }
    var el = this.grid.querySelector('[data-iso="' + iso + '"]');
    if (el) el.focus();
  };

  Calendar.prototype.shiftMonth = function (n) {
    var target = monthStart(this.cursor, n);
    if (target < monthStart(this.today) || target > this.lastMonth) return;
    this.cursor = target;
    this.focusISO = toISO(target);
    this.render();
    var el = this.grid.querySelector('[data-iso]:not([disabled])');
    if (el) el.focus();
  };

  Calendar.prototype.bind = function () {
    var self = this;

    this.grid.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-iso]');
      if (!btn) return;
      self.choose(btn.getAttribute('data-iso'), btn.getAttribute('data-kind'));
    });

    this.grid.addEventListener('keydown', function (e) {
      var btn = e.target.closest('[data-iso]');
      if (!btn) return;
      var iso = btn.getAttribute('data-iso');
      var d = fromISO(iso);
      var move = null;

      switch (e.key) {
        case 'ArrowLeft':  move = addDays(d, -1); break;
        case 'ArrowRight': move = addDays(d, 1); break;
        case 'ArrowUp':    move = addDays(d, -7); break;
        case 'ArrowDown':  move = addDays(d, 7); break;
        case 'Home':       move = addDays(d, -d.getUTCDay()); break;
        case 'End':        move = addDays(d, 6 - d.getUTCDay()); break;
        case 'PageUp':     self.shiftMonth(e.shiftKey ? -12 : -1); e.preventDefault(); return;
        case 'PageDown':   self.shiftMonth(e.shiftKey ? 12 : 1); e.preventDefault(); return;
        case 'Escape':     self.input.focus(); e.preventDefault(); return;
        case 'Enter':
        case ' ':
          self.choose(iso, btn.getAttribute('data-kind'));
          e.preventDefault();
          return;
        default: return;
      }

      // Stop the arrow keys scrolling the page out from under the grid. This is the
      // only preventDefault here that touches navigation, and none of it goes near
      // the form: every button above is type="button".
      e.preventDefault();
      self.moveFocus(toISO(move));
    });

    if (this.prev) {
      this.prev.addEventListener('click', function () { self.shiftMonth(-1); });
    }
    if (this.next) {
      this.next.addEventListener('click', function () { self.shiftMonth(1); });
    }

    // Typing in the native input keeps the grid in step, so the two read as one field.
    ['change', 'input'].forEach(function (evt) {
      self.input.addEventListener(evt, function () { self.syncFromInput(); });
    });
  };

  /* Reflects whatever the input currently holds: highlight, advisory note, and the
     scheduling flag Julie sees in her inbox. Never rejects, never blocks. */
  Calendar.prototype.syncFromInput = function () {
    var v = this.input.value;
    var reason = v && this.blackouts[v] ? this.blackouts[v] : '';
    var rushed = !!(v && this.leadISO && v < this.leadISO && !reason);

    if (this.note) {
      if (reason) {
        this.note.textContent = 'That day is blocked: ' + reason +
          '. Send it anyway if you like, and Julie will come straight back to you with ' +
          'the closest day she can do. If it is a birthday or something that cannot ' +
          'move, say so and she will try to make that day work.';
      }
      this.note.hidden = !reason;
    }

    if (this.flag) {
      this.flag.value = reason ? reason
        : (rushed ? 'Sooner than the usual ' + this.leadDays + ' days' : '');
    }

    var d = fromISO(v);
    if (d && !sameMonth(d, this.cursor)) this.cursor = monthStart(d);
    if (v) this.focusISO = v;
    this.render();
  };

  /* ---------- boot ---------- */

  function init(scope) {
    var nodes = (scope || document).querySelectorAll('[data-dda2-calendar]');
    Array.prototype.forEach.call(nodes, function (n) {
      if (n.__dda2Bound) return;
      n.__dda2Bound = true;
      new Calendar(n);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  // The theme editor re-renders a section without reloading the page.
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
